import type { BlobRecord, OutboxRecord } from "../outbox/types";
import { isUnfinishedCapture, payloadStr } from "../components/entry-meta";

export function hasVoice(parts: BlobRecord[]): boolean {
  return parts.some((part) => part.role === "voice_annotation");
}

export function canRetryTranscription(
  transcript: string | undefined,
  parts: BlobRecord[],
  transportAvailable: boolean,
): boolean {
  return transportAvailable && transcript === "" && hasVoice(parts);
}

/** Android's installed Chrome PWA does not reliably emit `online` when the
 * radio is restored from quick settings. Treat every return-to-app signal as a
 * connectivity wake as well; the caller's state guard keeps this idempotent
 * and only a blank durable voice capture can actually enter transcription. */
export function installConnectivityRetry(retry: () => void): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};

  const wakeIfOnline = (): void => {
    if (window.navigator.onLine !== false) retry();
  };
  const onVisibility = (): void => {
    if (document.visibilityState === "visible") wakeIfOnline();
  };

  window.addEventListener("online", wakeIfOnline);
  window.addEventListener("focus", wakeIfOnline);
  window.addEventListener("pageshow", wakeIfOnline);
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    window.removeEventListener("online", wakeIfOnline);
    window.removeEventListener("focus", wakeIfOnline);
    window.removeEventListener("pageshow", wakeIfOnline);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}

/** Restored transcribe-time HELD records deliberately have no transcript key.
 * Return a blank editable value only when their durable blobs prove they are a
 * voice capture; photo-only records must retain `undefined`. */
export function recoveredTranscript(
  record: OutboxRecord,
  parts: BlobRecord[],
): string | undefined {
  return payloadStr(record, "transcript")
    ?? (isUnfinishedCapture(record) && hasVoice(parts) ? "" : undefined);
}
